import React, {useState} from "react";
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    Typography,
    TableBody,
    Box,
    IconButton,
    Collapse,
    Button,
} from "@mui/material";
import {Create, KeyboardArrowDown, KeyboardArrowRight} from "@mui/icons-material";
import {Trans, useTranslation} from "react-i18next";
import dayjs from "dayjs";
import {Notification, NotificationStatus} from "../../../types/domain/notification";
import DOMPurify from "dompurify";

interface AnnouncementsTableProps {
    announcements: Notification[];
    total: number;
    page: number;
    rowsPerPage: number;
    onPageChange?: (newPage: number) => void;
    onRowsPerPageChange?: (newSize: number) => void;
    editAnnouncement: (notification: Notification) => void;
    setAnnouncements: (notifications: Notification[]) => void;
    changeNotificationStatus: (id: string, status: NotificationStatus) => Promise<void>;
}

export const AnnouncementsTable: React.FC<AnnouncementsTableProps> = ({
    announcements,
    page,
    rowsPerPage,
    editAnnouncement,
}) => {
    const {t} = useTranslation();
    const [openRowId, setOpenRowId] = useState<string | null>(null);

    const toggleRow = (id: string) => {
        setOpenRowId((prev) => (prev === id ? null : id));
    };

    return announcements?.length ? (
        <>
            <TableContainer sx={{overflowY: "auto", backgroundColor: "white"}}>
                <Table>
                    <TableHead sx={{backgroundColor: "#F3F5FA"}}>
                        <TableRow>
                            <TableCell />
                            <TableCell>
                                <Typography fontWeight="bold">#</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography fontWeight="bold">{t("announcements_page.title")}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography fontWeight="bold">{t("announcements_page.startTime")}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography fontWeight="bold">{t("announcements_page.viewed")}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography fontWeight="bold">{t("announcements_page.status")}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography fontWeight="bold">{t("announcements_page.actions")}</Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {announcements?.map((announcement, index) => {
                            const isOpen = openRowId === announcement.id;
                            return (
                                <React.Fragment key={announcement.id}>
                                    <TableRow>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    if (announcement?.id) {
                                                        toggleRow(announcement.id);
                                                    }
                                                }}
                                            >
                                                {isOpen ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                                        <TableCell>{announcement.name}</TableCell>
                                        <TableCell sx={{whiteSpace: "nowrap"}}>
                                            {announcement.status === NotificationStatus.SCHEDULED ||
                                            announcement.status === NotificationStatus.SENT
                                                ? dayjs.utc(announcement.startTime).format("MMMM D, YYYY h:mm A")
                                                : ""}
                                        </TableCell>
                                        <TableCell>{announcement.seen ?? "0"}</TableCell>
                                        <TableCell>{announcement.status}</TableCell>

                                        <TableCell>
                                            <Button
                                                onClick={() => {
                                                    editAnnouncement(announcement);
                                                }}
                                                size="small"
                                                variant="outlined"
                                                startIcon={<Create />}
                                            >
                                                {t("announcements_page.edit")}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow sx={{backgroundColor: "#F3F5FA"}}>
                                        <TableCell colSpan={7} sx={{paddingBottom: 0, paddingTop: 0}}>
                                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                <Box
                                                    sx={{
                                                        backgroundColor: "white",
                                                        borderRadius: "4px",
                                                        padding: "1.5rem",
                                                        border: "1px solid #e0e0e0",
                                                    }}
                                                    margin={2}
                                                    dangerouslySetInnerHTML={{
                                                        __html: DOMPurify.sanitize(
                                                            String(announcement?.metaData?.content) || "",
                                                        ),
                                                    }}
                                                />
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    ) : (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            sx={{marginTop: "2rem"}}
        >
            <Typography variant="h3" color="textSecondary">
                <Trans
                    i18nKey="announcements_page.no_announcements_message"
                    components={{
                        button: (
                            <Typography
                                component="span"
                                sx={{
                                    color: "primary.main",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    fontSize: "1.1rem",
                                }}
                            />
                        ),
                    }}
                />
            </Typography>
        </Box>
    );
};
